import React, { useMemo, useRef, useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { db } from "../../firebase";
import { useFamilyData } from "../../hooks/useFamilyData";
import { useToast } from "../../components/ui/ToastProvider";
import { mapFirebaseError } from "../../utils/userErrors";

export default function CalendarCard({ familyId, familySettings, canManage }) {
  const { showToast } = useToast();
  const { calendarEvents = [] } = useFamilyData(familyId);
  const titleRef = useRef(null);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [calendarError, setCalendarError] = useState("");
  const isPro = familySettings?.isPro || false;

  const sortedEvents = useMemo(() => {
    const toMs = (event) => {
      if (!event?.date) return Number.MAX_SAFE_INTEGER;
      const timePart = event.time || "23:59";
      const ms = new Date(`${event.date}T${timePart}`).getTime();
      return Number.isNaN(ms) ? Number.MAX_SAFE_INTEGER : ms;
    };
    return [...calendarEvents].sort((a, b) => toMs(a) - toMs(b));
  }, [calendarEvents]);

  const addEvent = async () => {
    if (!title.trim()) {
      setCalendarError("Event title is required.");
      return;
    }
    if (!date) {
      setCalendarError("Event date is required.");
      return;
    }
    try {
      setCalendarError("");
      await addDoc(collection(db, "families", familyId, "calendarEvents"), {
        title,
        date, 
        time: time || null,
        createdAt: new Date()
      });
      setTitle(""); setDate(""); setTime("");
      showToast({ type: "success", message: "Event added." });
      titleRef.current?.focus();
    } catch (err) {
      setCalendarError(mapFirebaseError(err, "Unable to add event."));
    }
  };

  return (
    <div className="glass parent-card" data-tour="parent-calendar-card">
      <div className="header-flex">
        <h2>Calendar</h2>
        {isPro && canManage && (
          <span className="badge" title="Calendar sync is configured in Settings > Calendar Sync.">
            Sync in Calendar Sync
          </span>
        )}
      </div>

      <div className="card-body">
        <div className="input-group">
          <input 
            placeholder="Event Title" 
            ref={titleRef}
            value={title} 
            data-tour="calendar-event-title-input"
            onChange={e => setTitle(e.target.value)} 
            disabled={!canManage}
          />
          <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
            <input
              type="date"
              value={date}
              data-tour="calendar-event-date-input"
              onChange={e => setDate(e.target.value)}
              disabled={!canManage}
            />
            <input
              type="time"
              value={time}
              data-tour="calendar-event-time-input"
              onChange={e => setTime(e.target.value)}
              disabled={!canManage}
            />
          </div>
          <button
            className="btn-nav success-btn"
            data-tour="calendar-event-add-button"
            onClick={addEvent}
            style={{ width: '100%', marginTop: '5px' }}
            disabled={!canManage}
          >
            Add Event
          </button>
          {calendarError && (
            <small className="pin-error" role="alert">
              {calendarError}
            </small>
          )}
        </div>

        <div className="event-list" style={{ marginTop: '15px' }}>
          {sortedEvents.length === 0 ? (
            <div style={{ opacity: 0.7, textAlign: "center", display: "grid", gap: 8 }}>
              <p style={{ margin: 0 }}>No events added yet.</p>
              {canManage && (
                <button className="btn-nav" type="button" onClick={() => titleRef.current?.focus()}>
                  Add your first event
                </button>
              )}
            </div>
          ) : (
            sortedEvents.slice(0, 5).map(e => (
              <div key={e.id} className="parent-list-item">
                <span>{e.title}</span>
                <small>{e.date} {e.time}</small>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
